import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { Listbox } from 'tio-ui/components/collections';
import Popover from 'tio-reimagined/components/popover';
import { eq, gt } from 'tio-ui/utilities';
import { formatCurrency, formatShortDate } from 'tio-reimagined/format';
import { htmlSafe } from '@ember/template';
import { t } from 'ember-intl';
import { PhCaretDown, PhBooks, PhStudent } from 'ember-phosphor-icons';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import BenefitHistorySummary from 'tio-common/components/tas/benefit-history-summary';
import {
  applicationAmountInfo as computeAmountInfo,
  getApplicationName,
  getHistoryDate,
  getInstanceName,
  getProgramName,
} from 'tio-employee/utils/tas/activity-helpers';
import type { AmountInfo } from 'tio-employee/utils/tas/activity-helpers';
import type { IntlService } from 'ember-intl';
import type PartnerService from 'tio-employee/services/partner';
import type {
  RecentApplication,
  ProgramInstance,
} from 'tio-common/utils/tuition-assistance/application-states';
import type { BenefitHistoryModel } from 'tio-employee/routes/authenticated/tas/benefit-history';

interface UnifiedHistoryItem {
  type: 'application' | 'instance';
  updatedAt: string;
  application?: RecentApplication;
  instance?: ProgramInstance;
}

interface BenefitHistorySignature {
  Args: {
    model: BenefitHistoryModel;
  };
}

export default class TasBenefitHistory extends Component<BenefitHistorySignature> {
  @service declare intl: IntlService;
  @service declare partner: PartnerService;

  @tracked selectedYear = '';

  get selectedYearValue() {
    return this.selectedYear || this.args.model.currentYear.toString();
  }

  get yearOptions() {
    const options: string[] = ['all'];

    // Add years in descending order
    for (const year of this.args.model.availableYears) {
      options.push(year.toString());
    }

    return options;
  }

  get yearLabel() {
    if (this.selectedYearValue === 'all') {
      return this.intl.t('tas.benefit_history.all_years');
    }
    const year = parseInt(this.selectedYearValue, 10);
    return `${year}-${year + 1}`;
  }

  getYearOptionLabel = (option: string) => {
    if (option === 'all') {
      return this.intl.t('tas.benefit_history.all_years');
    }
    const year = parseInt(option, 10);
    return `${year}-${year + 1}`;
  };

  get filteredApplications() {
    if (this.selectedYearValue === 'all') {
      return this.args.model.applications;
    }

    const yearFilter = parseInt(this.selectedYearValue, 10);
    const filtered = this.args.model.applications.filter((app) => {
      const dateStr = getHistoryDate(app);
      if (!dateStr) {
        return false;
      }
      const year = new Date(dateStr).getFullYear();
      const matches = year === yearFilter || year === yearFilter + 1;

      // Academic year spans two calendar years (e.g., 2025-2026 includes 2025 and 2026)
      return matches;
    });
    return filtered;
  }

  get filteredProgramInstances() {
    if (this.selectedYearValue === 'all') {
      return this.args.model.programInstances;
    }

    const yearFilter = parseInt(this.selectedYearValue, 10);
    const filteredInstances = this.args.model.programInstances
      .map((instance) => {
        // Filter child applications by year
        const filteredChildren = instance.childApplications.filter((app) => {
          const dateStr = getHistoryDate(app);
          if (!dateStr) return false;
          const year = new Date(dateStr).getFullYear();
          return year === yearFilter || year === yearFilter + 1;
        });

        const instanceDateStr = getHistoryDate(instance.instanceApplication);
        const instanceMatchesYear = (() => {
          if (!instanceDateStr) return false;
          const year = new Date(instanceDateStr).getFullYear();
          return year === yearFilter || year === yearFilter + 1;
        })();

        // Return instance if it or its children match the year filter
        if (filteredChildren.length > 0 || instanceMatchesYear) {
          return {
            instanceApplication: instance.instanceApplication,
            childApplications: filteredChildren,
          };
        }

        return null;
      })
      .filter((instance): instance is NonNullable<typeof instance> => instance !== null);

    return filteredInstances;
  }

  get sortedHistoryItems() {
    const items: UnifiedHistoryItem[] = [];

    // Add flat applications
    for (const app of this.filteredApplications) {
      items.push({
        type: 'application',
        updatedAt: app.updatedAt || '',
        application: app,
      });
    }

    // Add Type 1 and Type 4.2/4.3 instances
    for (const instance of this.filteredProgramInstances) {
      items.push({
        type: 'instance',
        updatedAt: instance.instanceApplication.updatedAt || '',
        instance,
      });
    }

    // Sort by updated_at descending (most recent first)
    return items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  get hasHistory() {
    return this.sortedHistoryItems.length > 0;
  }

  get externalSpendLabel() {
    return (
      this.partner.settingModules?.externalSpendDescriptor ||
      this.intl.t('tas.dashboard.annual_benefits.external')
    );
  }

  /**
   * Compute amount info for a RecentApplication (OpenSearch fields, amounts in cents).
   */
  applicationAmountInfo = (app: RecentApplication): AmountInfo | null => {
    return computeAmountInfo(app);
  };

  @tracked yearPopoverOpen = false;

  openYearPopover = () => {
    this.yearPopoverOpen = true;
  };

  closeYearPopover = () => {
    this.yearPopoverOpen = false;
  };

  handleYearSelect = (key: string) => {
    this.selectedYear = key;
    this.closeYearPopover();
  };

  <template>
    <VStack>
      {{! Benefit History Summary }}
      <Section>
        <:header>
          <Header>{{t "tas.benefit_history.title"}}</Header>
        </:header>
        <:body>
          <BenefitHistorySummary
            @limitsStatusPrevious={{@model.limitsStatusPrevious}}
            @limitsStatusCurrent={{@model.limitsStatusCurrent}}
            @limitsStatusNext={{@model.limitsStatusNext}}
            @externalSpendLabel={{this.externalSpendLabel}}
          />
        </:body>
      </Section>

      {{! Activity Section }}
      <Section>
        <:header>
          <div class="flex items-center justify-between">
            <Header>{{t "tas.benefit_history.activity_title"}}</Header>
            <Popover
              @isOpen={{this.yearPopoverOpen}}
              @onOpen={{this.openYearPopover}}
              @onClose={{this.closeYearPopover}}
            >
              <:trigger as |pop|>
                <button
                  type="button"
                  class="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50"
                  data-test-benefit-history-year-filter
                  {{pop.register}}
                  {{on "click" pop.toggle}}
                >
                  {{t "tas.benefit_history.view"}}
                  {{this.yearLabel}}
                  <PhCaretDown @size={{12}} />
                </button>
              </:trigger>
              <:body>
                <div class="overflow-hidden">
                  <Listbox
                    @isKeyboardEventsEnabled={{true}}
                    @allowEmpty={{true}}
                    @selectedKeys={{array this.selectedYearValue}}
                    @onAction={{this.handleYearSelect}}
                    @intent="primary"
                    @selectionMode="single"
                    @class="w-full"
                    data-test-benefit-history-year-options
                    as |l|
                  >
                    {{#each this.yearOptions as |option|}}
                      <l.Item class="[&>span]:text-sm" @key={{option}}>
                        {{this.getYearOptionLabel option}}
                      </l.Item>
                    {{/each}}
                  </Listbox>
                </div>
              </:body>
            </Popover>
          </div>
        </:header>
        <:body>
          {{#if this.hasHistory}}
            <div class="grid grid-cols-1 gap-4">
              {{#each this.sortedHistoryItems as |item|}}
                {{#if (eq item.type "instance")}}
                  {{#let item.instance as |instance|}}
                    {{#if instance}}
                      {{#if (gt instance.childApplications.length 0)}}
                        {{! Type 4 instance WITH children — bordered group container }}
                        <div
                          class="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                        >
                          {{! Program header row }}
                          <div
                            class="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200"
                          >
                            <div class="flex items-center gap-2">
                              <Heading @level={{3}}>
                                {{getInstanceName instance.instanceApplication}}
                              </Heading>
                              <StatePill
                                @state={{instance.instanceApplication.tasProgramInstanceStateKeyword}}
                              />
                            </div>
                            <LinkTo
                              @route="authenticated.tas.programs.show"
                              @model={{instance.instanceApplication.tasProgramInstanceIdKeyword}}
                              class="tio-copy text-sm font-semibold"
                            >
                              {{t "tas.benefit_history.view_program"}}
                            </LinkTo>
                          </div>
                          {{! Child application cards }}
                          <div class="divide-y divide-gray-100">
                            {{#each instance.childApplications as |childApp|}}
                              <LinkTo
                                @route="authenticated.tas.applications.show"
                                @model={{childApp.tasApplicationIdKeyword}}
                                class="block p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                              >
                                <div class="flex items-start gap-4">
                                  <div
                                    class="flex h-18 w-18 shrink-0 items-center justify-center rounded-xl bg-gray-200"
                                  >
                                    <PhBooks class="h-12 w-12 text-gray-600" />
                                  </div>
                                  <div class="flex flex-1 flex-col gap-1 min-w-0">
                                    <div class="flex items-center gap-2 flex-wrap">
                                      <Heading @level={{3}} class="truncate">
                                        {{getApplicationName childApp}}
                                      </Heading>
                                      <StatePill @state={{childApp.tasApplicationStateKeyword}} />
                                    </div>
                                    <Text @variant="muted" class="text-sm">
                                      {{#if childApp.updatedAt}}
                                        {{t
                                          "tuition_assistance.reimagined.dashboard.activity_last_action"
                                          date=(formatShortDate childApp.updatedAt)
                                        }}
                                      {{/if}}
                                    </Text>
                                  </div>
                                  <div class="hidden shrink-0 gap-3 md:flex">
                                    {{#let (this.applicationAmountInfo childApp) as |amountInfo|}}
                                      {{#if amountInfo}}
                                        {{#if (gt amountInfo.reimbursementAmount 0)}}
                                          <div
                                            class="rounded-xl bg-rose-100 px-4 py-1.5 text-center min-w-30"
                                          >
                                            <Text class="text-xs font-medium text-gray-600">
                                              {{t
                                                "tuition_assistance.reimagined.dashboard.metric_reimbursement"
                                              }}
                                            </Text>
                                            <div class="text-base font-bold text-gray-900">
                                              {{formatCurrency amountInfo.reimbursementAmount}}
                                            </div>
                                            <Text class="text-xs text-rose-800">
                                              {{t amountInfo.reimbursementLabelKey}}
                                            </Text>
                                          </div>
                                        {{/if}}
                                        {{#if amountInfo.hasCourseCash}}
                                          <div
                                            class="rounded-xl bg-teal-100 px-4 py-1.5 text-center min-w-30"
                                          >
                                            <Text class="text-xs font-medium text-gray-600">
                                              {{htmlSafe
                                                (t
                                                  "tuition_assistance.reimagined.dashboard.metric_course_cash"
                                                )
                                              }}
                                            </Text>
                                            <div class="text-base font-bold text-gray-900">
                                              {{formatCurrency amountInfo.courseCashAmount}}
                                            </div>
                                            <Text class="text-xs text-teal-800">
                                              {{t amountInfo.courseCashLabelKey}}
                                            </Text>
                                          </div>
                                        {{/if}}
                                      {{/if}}
                                    {{/let}}
                                  </div>
                                </div>
                              </LinkTo>
                            {{/each}}
                          </div>
                        </div>
                      {{else}}
                        {{! Program instance with NO children — standalone graduate card }}
                        <LinkTo
                          @route="authenticated.tas.programs.show"
                          @model={{instance.instanceApplication.tasProgramInstanceIdKeyword}}
                          class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm block hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div class="flex items-start gap-4">
                            <div
                              class="flex h-18 w-18 shrink-0 items-center justify-center rounded-xl bg-gray-200"
                            >
                              <PhStudent class="h-12 w-12 text-gray-600" />
                            </div>
                            <div class="flex flex-1 flex-col gap-1 min-w-0">
                              <div class="flex items-center gap-2 flex-wrap">
                                <Heading @level={{3}} class="truncate">
                                  {{getInstanceName instance.instanceApplication}}
                                </Heading>
                                <StatePill
                                  @state={{instance.instanceApplication.tasProgramInstanceStateKeyword}}
                                />
                              </div>
                              <Text @variant="muted" class="text-sm">
                                <span>{{getProgramName instance.instanceApplication}}</span>
                                {{#if instance.instanceApplication.updatedAt}}
                                  <span class="mx-1">•</span>
                                  <span>{{t
                                      "tuition_assistance.reimagined.dashboard.activity_last_action"
                                      date=(formatShortDate instance.instanceApplication.updatedAt)
                                    }}</span>
                                {{/if}}
                              </Text>
                              {{#if
                                instance.instanceApplication.tasProgramInstanceFieldSchoolInstitutionNameKeyword
                              }}
                                <Text @variant="muted" class="text-sm">
                                  {{instance.instanceApplication.tasProgramInstanceFieldSchoolInstitutionNameKeyword}}
                                </Text>
                              {{/if}}
                            </div>
                          </div>
                        </LinkTo>
                      {{/if}}
                    {{/if}}
                  {{/let}}
                {{else}}
                  {{! Flat application (Type 2/3) — application card }}
                  {{#let item.application as |application|}}
                    {{#if application}}
                      <LinkTo
                        @route="authenticated.tas.applications.show"
                        @model={{application.tasApplicationIdKeyword}}
                        class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm block hover:shadow-md transition-shadow cursor-pointer"
                      >
                        <div class="flex items-start gap-4">
                          <div
                            class="flex h-18 w-18 shrink-0 items-center justify-center rounded-xl bg-gray-200"
                          >
                            <PhBooks class="h-12 w-12 text-gray-600" />
                          </div>
                          <div class="flex flex-1 flex-col gap-1 min-w-0">
                            <div class="flex items-center gap-2 flex-wrap">
                              <Heading @level={{3}} class="truncate">
                                {{getApplicationName application}}
                              </Heading>
                              <StatePill @state={{application.tasApplicationStateKeyword}} />
                            </div>
                            <Text @variant="muted" class="text-sm">
                              <span>{{getProgramName application}}</span>
                              {{#if application.updatedAt}}
                                <span class="mx-1">•</span>
                                <span>{{t
                                    "tuition_assistance.reimagined.dashboard.activity_last_action"
                                    date=(formatShortDate application.updatedAt)
                                  }}</span>
                              {{/if}}
                            </Text>
                          </div>
                          <div class="hidden shrink-0 gap-3 md:flex">
                            {{#let (this.applicationAmountInfo application) as |amountInfo|}}
                              {{#if amountInfo}}
                                {{#if (gt amountInfo.reimbursementAmount 0)}}
                                  <div
                                    class="rounded-xl bg-rose-100 px-4 py-1.5 text-center min-w-30"
                                  >
                                    <Text class="text-xs font-medium text-gray-600">
                                      {{t
                                        "tuition_assistance.reimagined.dashboard.metric_reimbursement"
                                      }}
                                    </Text>
                                    <div class="text-base font-bold text-gray-900">
                                      {{formatCurrency amountInfo.reimbursementAmount}}
                                    </div>
                                    <Text class="text-xs text-rose-800">
                                      {{t amountInfo.reimbursementLabelKey}}
                                    </Text>
                                  </div>
                                {{/if}}
                                {{#if amountInfo.hasCourseCash}}
                                  <div
                                    class="rounded-xl bg-teal-100 px-4 py-1.5 text-center min-w-30"
                                  >
                                    <Text class="text-xs font-medium text-gray-600">
                                      {{htmlSafe
                                        (t
                                          "tuition_assistance.reimagined.dashboard.metric_course_cash"
                                        )
                                      }}
                                    </Text>
                                    <div class="text-base font-bold text-gray-900">
                                      {{formatCurrency amountInfo.courseCashAmount}}
                                    </div>
                                    <Text class="text-xs text-teal-800">
                                      {{t amountInfo.courseCashLabelKey}}
                                    </Text>
                                  </div>
                                {{/if}}
                              {{/if}}
                            {{/let}}
                          </div>
                        </div>
                      </LinkTo>
                    {{/if}}
                  {{/let}}
                {{/if}}
              {{/each}}
            </div>
          {{else}}
            <div class="text-center py-12 text-gray-500">
              {{#if (eq this.selectedYearValue "all")}}
                {{t "tas.benefit_history.no_activity"}}
              {{else}}
                {{t "tas.benefit_history.no_activity_for_year" year=this.selectedYearValue}}
              {{/if}}
            </div>
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}
